import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Minus, Plus, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AddressService, CustomerAddress } from '@/services/address.service';
import { AddAddressDrawer, AddAddressFormData } from '@/components/AddAddressDrawer';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { LoginDialog } from '@/components/LoginDialog';

const Cart = () => {
  const navigate = useNavigate();
  const { cart, cartData, updateQuantity, removeFromCart, isLoading, refreshCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [showAddAddressDialog, setShowAddAddressDialog] = useState(false);
  const [deliveryAddressId, setDeliveryAddressId] = useState<number | null>(null);
  const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
  const [sameAsDelivery, setSameAsDelivery] = useState(false);
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);

  // Refresh cart when component mounts
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  // Memoized fetch addresses function
  const fetchAddresses = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setAddressesLoading(true);
      const addressList = await AddressService.getCustomerAddresses(user.id);
      setAddresses(addressList);
      
      // Auto-select default address if available
      const defaultAddr = addressList.find(addr => addr.default_address === 1);
      if (defaultAddr && !deliveryAddressId) {
        setDeliveryAddressId(defaultAddr.id);
        setBillingAddressId(defaultAddr.id);
        setSameAsDelivery(true);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setAddressesLoading(false);
    }
  }, [user?.id, deliveryAddressId]);

  // Fetch addresses when dialog opens
  useEffect(() => {
    if (showAddressDialog && user?.id) {
      fetchAddresses();
    }
  }, [showAddressDialog, user?.id, fetchAddresses]);

  // Handle adding a new address
  const handleAddAddress = async (formData: AddAddressFormData) => {
    if (!isAuthenticated || !user?.id) {
      setShowLoginDialog(true);
      return;
    }

    try {
      setAddingAddress(true);
      
      // Format address1 as array (address line 1, full address, landmark)
      const address1Array = [
        formData.address1,
        `${formData.address1}${formData.address2 ? ', ' + formData.address2 : ''}, ${formData.city}`,
        formData.landmark || ''
      ].filter(Boolean); // Remove empty strings
      
      const newAddress = await AddressService.createAddress({
        first_name: formData.first_name,
        last_name: formData.last_name,
        company_name: formData.company_name || undefined,
        address1: address1Array,
        city: formData.city,
        country: formData.country,
        country_name: formData.country === 'IN' ? 'India' : formData.country,
        phone: formData.phone,
        postcode: formData.postcode,
        state: formData.state,
        prefer_time: formData.delivery_time || undefined,
        prefer_delivery: formData.address_type, // 'home' or 'office'
        delivery_instructions: formData.delivery_instructions || undefined,
        // Optional: Add geolocation if available
        // longitude: ...,
        // latitude: ...,
        // state_code: ...,
      });

      toast({
        title: 'Success',
        description: 'Address added successfully',
      });

      // Refresh addresses and close dialog
      await fetchAddresses();
      setShowAddAddressDialog(false);
      
      // Auto-select the newly created address for both delivery and billing
      setDeliveryAddressId(newAddress.id);
      setBillingAddressId(newAddress.id);
      setSameAsDelivery(true);
    } catch (error) {
      console.error('Error adding address:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add address',
      });
    } finally {
      setAddingAddress(false);
    }
  };

  // Helper function to get final price
  const getFinalPrice = (product: any) => {
    console.log('Calculating final price for product:', product.total - product.base_total, product.base_total,product);
    return  parseFloat(product.base_total) + (parseFloat(product.total) - parseFloat(product.base_total));};
  const taxAmount:any = cart.reduce((sum, item) => sum + (parseFloat(item.product.total) - parseFloat(item.product.base_total)) * item.quantity, 0);
  const subToatal = cart.reduce((sum, item) => sum + parseFloat(item.product.base_total),0);
  // Helper function to get brand name
  const getBrandName = (product: any) => product.brand_label || product.brand_name || '';

  const subtotal = cart.reduce((sum, item) => sum + getFinalPrice(item.product) * item.quantity, 0);
  const deliveryFee = 0;
  const total:any = subtotal + deliveryFee;

  const handleSelectDeliveryAddress = (addressId: number) => {
    setDeliveryAddressId(addressId);
    if (sameAsDelivery) {
      setBillingAddressId(addressId);
    }
  };

  const handleSelectBillingAddress = (addressId: number) => {
    setBillingAddressId(addressId);
    setSameAsDelivery(false);
  };

  const handleSameAsDeliveryChange = (checked: boolean) => {
    setSameAsDelivery(checked);
    if (checked && deliveryAddressId) {
      setBillingAddressId(deliveryAddressId);
    }
  };

  const getAddressById = (id: number | null) => {
    return addresses.find(addr => addr.id === id) || null;
  };

  const formatAddress = (address: CustomerAddress) => {
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.postcode
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="sticky top-0 bg-background border-b z-10 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold">Cart</h1>
        </div>
      </div>

      <div className="container max-w-2xl mx-auto px-4 py-6 space-y-4">
        {isLoading && cart.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : cart.length === 0 ? (
          <Card className="p-8">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
              <p className="text-muted-foreground mb-4">Add some products to get started</p>
              <Button onClick={() => navigate('/')}>
                Continue Shopping
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Coupon Section - Hidden for now */}
            {/* <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Saving Corner</p>
                  <p className="font-semibold">Apply Coupon</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </Card> */}

            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Added Products</h3>
                <p className="text-sm text-muted-foreground">{cart.length} Item{cart.length !== 1 ? 's' : ''}</p>
              </div>

          <div className="space-y-4">
            {cart.map((item) => {
              const finalPrice = getFinalPrice(item.product);
              const brandName = getBrandName(item.product);
              console.log('Cart Item Product:', item);
              // Get image with proper fallback
              const getImageSrc = () => {
                if (item.product.base_image && item.product.base_image !== null) {
                  const baseUrl = item.product.s3_base_url || item.product.media_base_url || '';
                  return `${baseUrl}${item.product.base_image}`;
                }
                if (item.product.thumb_image && item.product.thumb_image !== null) {
                  const baseUrl = item.product.s3_base_url || item.product.media_base_url || '';
                  return `${baseUrl}${item.product.thumb_image}`;
                }
                if (item.product.default_image) {
                  if (item.product.default_image.startsWith('vendor/') || item.product.default_image.startsWith('product/')) {
                    return `https://sparts.in:8443/${item.product.default_image}`;
                  }
                  return item.product.default_image;
                }
                return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="16" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              };
              
              const imageSrc = getImageSrc();

              return (
                <div key={item.product.id} className="flex gap-4">
                  <div className="w-16 h-16 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                    <img
                      src={imageSrc}
                      alt={item.product.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="16" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{item.product.name}</h4>
                        <p className="text-xs text-muted-foreground">{brandName}</p>
                        {/* <p className="text-xs text-muted-foreground">1 pc x {item.quantity}</p> */}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="p-1 hover:bg-destructive/10 hover:text-destructive rounded-md transition-colors"
                        title="Remove item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 border rounded-md">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className="p-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={item.quantity <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="px-2 text-sm font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        className="p-1 hover:bg-muted"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">₹{parseFloat(item?.product?.base_total).toFixed(2)}</p>
                      {/* {item.product.price > finalPrice && (
                        <p className="text-xs line-through text-muted-foreground">
                          ₹{item.product.cart_item_base_total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      )}
                      {item.product.price > finalPrice && item.product.cart_item_total === undefined && (
                        <p className="text-xs line-through text-muted-foreground">
                          ₹{(item.product.price * item.quantity).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="font-semibold mb-4">Bill Details</h3>
          
          {!isAuthenticated && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm text-amber-800 font-medium">Please login to add delivery address</p>
              <Button
                variant="link"
                className="h-auto p-0 text-amber-900 font-semibold"
                onClick={() => setShowLoginDialog(true)}
              >
                Login Now
              </Button>
            </div>
          )}

          <div className="flex gap-2 mb-4">
            <Button
              variant="secondary"
              className="flex-1 justify-center"
              onClick={() => {
                if (!isAuthenticated) {
                  setShowLoginDialog(true);
                } else {
                  setShowAddAddressDialog(true);
                }
              }}
            >
              Add Address
            </Button>
            <Button
              variant="default"
              className="flex-1 justify-center"
              onClick={() => {
                if (!isAuthenticated) {
                  setShowLoginDialog(true);
                } else {
                  setShowAddressDialog(true);
                }
              }}
            >
              Select Address
            </Button>
          </div>

          {deliveryAddressId && (
            <div className="mb-4 space-y-3">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-semibold text-sm mb-1">DELIVERY ADDRESS</p>
                <p className="text-xs text-muted-foreground">{formatAddress(getAddressById(deliveryAddressId)!)}</p>
              </div>
              {!sameAsDelivery && billingAddressId && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-semibold text-sm mb-1">BILLING ADDRESS</p>
                  <p className="text-xs text-muted-foreground">{formatAddress(getAddressById(billingAddressId)!)}</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">SubTotal </span>
              <span className="font-medium">₹{subToatal}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">GST </span>
              <span className="font-medium">₹{parseFloat(taxAmount).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delivery Fee</span>
              <span className="font-medium">₹{deliveryFee.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>To Pay</span>
              <span>₹{parseFloat(total).toFixed(2)}</span>
            </div>
          </div>
        </Card>

        {deliveryAddressId && billingAddressId && (
          <Button
            className="w-full"
            size="lg"
            onClick={() => navigate('/payment', { 
              state: { 
                deliveryAddressId,
                billingAddressId
              } 
            })}
          >
            Proceed to Pay
          </Button>
        )}
        </>
        )}
      </div>

      <Sheet open={showAddressDialog} onOpenChange={setShowAddressDialog}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
          <SheetHeader className="p-6 border-b">
            <SheetTitle>Select Address</SheetTitle>
          </SheetHeader>
          
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Only show Add New Address button if there are no addresses */}
              {!addressesLoading && addresses.length === 0 && (
                <Button
                  variant="outline"
                  className="w-full justify-between h-auto py-3"
                  onClick={() => {
                    setShowAddressDialog(false);
                    setShowAddAddressDialog(true);
                  }}
                >
                  <span>Add New Address</span>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              )}

              {/* Show address sections only if there are addresses */}
              {addresses.length > 0 && (
                <>
                  {addressesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Delivery Address Section */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold">DELIVERY ADDRESS</h4>
                        <div className="space-y-2">
                          {addresses.map((address) => (
                            <button
                              key={`delivery-${address.id}`}
                              onClick={() => handleSelectDeliveryAddress(address.id)}
                              className={`w-full text-left p-3 border rounded-lg hover:bg-muted transition-colors ${
                                deliveryAddressId === address.id ? 'border-primary bg-primary/5' : ''
                              }`}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <p className="font-semibold text-sm">
                                  {address?.prefer_delivery?.toUpperCase()}
                                </p>
                                {address.default_address  && (
                                  <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mb-1">
                                {address.first_name} {address.last_name}
                              </p>
                              <p className="text-xs text-muted-foreground">{formatAddress(address)}</p>
                              {address.phone && (
                                <p className="text-xs text-muted-foreground mt-1">Phone: {address.phone}</p>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Checkbox for same as delivery */}
                      <div className="flex items-center space-x-2 py-2">
                        <Checkbox 
                          id="sameAsDelivery" 
                          checked={sameAsDelivery}
                          onCheckedChange={handleSameAsDeliveryChange}
                        />
                        <Label 
                          htmlFor="sameAsDelivery"
                          className="text-sm font-medium cursor-pointer"
                        >
                          Billing address is same as delivery address
                        </Label>
                      </div>

                      {/* Billing Address Section - only show if not same as delivery */}
                      {!sameAsDelivery && (
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold">BILLING ADDRESS</h4>
                          <div className="space-y-2">
                            {addresses.map((address) => (
                              <button
                                key={`billing-${address.id}`}
                                onClick={() => handleSelectBillingAddress(address.id)}
                                className={`w-full text-left p-3 border rounded-lg hover:bg-muted transition-colors ${
                                  billingAddressId === address.id ? 'border-primary bg-primary/5' : ''
                                }`}
                              >
                                <div className="flex items-start justify-between mb-1">
                                  <p className="font-semibold text-sm">
                                     {address?.prefer_delivery?.toUpperCase()}
                                  </p>
                                  {address.default_address === 1 && (
                                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                                      Default
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mb-1">
                                  {address.first_name} {address.last_name}
                                </p>
                                <p className="text-xs text-muted-foreground">{formatAddress(address)}</p>
                                {address.phone && (
                                  <p className="text-xs text-muted-foreground mt-1">Phone: {address.phone}</p>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Confirm Button */}
                    </div>
                  )}
                </>
              )}

              {/* Show loading state when there are no addresses yet */}
              {addressesLoading && addresses.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
            </div>
          </div>

          {/* Fixed bottom button */}
          {addresses.length > 0 && (
            <div className="border-t p-6 bg-background">
              <Button 
                className="w-full" 
                onClick={() => setShowAddressDialog(false)}
                disabled={!deliveryAddressId || !billingAddressId}
              >
                Confirm Address Selection
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Address Drawer */}
      <AddAddressDrawer
        open={showAddAddressDialog}
        onOpenChange={setShowAddAddressDialog}
        onSubmit={handleAddAddress}
        isLoading={addingAddress}
      />

      {/* Login Dialog */}
      <LoginDialog open={showLoginDialog} onOpenChange={setShowLoginDialog} />
    </div>
  );
};

export default Cart;
